import type { ReactNode } from "react";
import { Facebook, Instagram, Linkedin, Mail, Phone, Twitter } from "lucide-react";

const LOYYAL_LOGO_URL = "/offers/loyyalLogoNew.png";

const SOCIAL_LINKS = [
  { icon: Facebook, label: "Facebook", href: "#" },
  { icon: Twitter, label: "Twitter", href: "#" },
  { icon: Instagram, label: "Instagram", href: "#" },
  { icon: Linkedin, label: "LinkedIn", href: "#" },
];

const CONTACT_ADDRESSES = [
  {
    flag: "🇦🇪",
    text: "Office 3205, HDS Tower, Cluster F, JLT, Dubai, UAE",
  },
  {
    flag: "🇺🇸",
    text: "1311 Delaware Avenue, Suite 1, Wilmington, Delaware, US 19806",
  },
  {
    flag: "🇸🇬",
    text: "68 Circular Road, #02-01, Singapore 049422",
  },
  {
    flag: "🇶🇦",
    text: "Office 3, Ground Floor Al Maha Complex, Mohammed Bin Thani St, Doha, Qatar",
  },
];

function ContactRow({
  icon,
  children,
}: {
  icon: ReactNode;
  children: ReactNode;
}) {
  return (
    <li className="flex gap-3">
      <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center text-slate-300">
        {icon}
      </span>
      <span className="text-sm leading-relaxed text-slate-400">{children}</span>
    </li>
  );
}

export function StorefrontFooter() {
  return (
    <footer className="border-t border-slate-200/60 bg-slate-900 text-slate-300">
      <div className="w-full px-4 py-14 sm:px-6 lg:px-10 xl:px-16">
        <div className="grid gap-10 lg:grid-cols-12 lg:gap-12">
          <div className="lg:col-span-5">
            <a href="/offers/storefront" className="inline-block">
              <img
                src={LOYYAL_LOGO_URL}
                alt="Loyyal"
                className="h-10 w-auto max-w-[160px] object-contain object-left"
              />
            </a>
            <p className="mt-4 max-w-xl text-sm leading-relaxed text-slate-400">
              Your premium destination for exclusive deals, rewards, and
              member-only promotions from trusted brand partners worldwide.
            </p>
            <div className="mt-6 flex gap-3">
              {SOCIAL_LINKS.map(({ icon: Icon, label, href }) => (
                <a
                  key={label}
                  href={href}
                  aria-label={label}
                  className="flex h-10 w-10 items-center justify-center border border-slate-700 bg-slate-800 text-slate-400 transition-colors hover:border-violet-500 hover:text-white"
                >
                  <Icon className="h-4 w-4" />
                </a>
              ))}
            </div>
          </div>

          <div className="lg:col-span-5 lg:col-start-8">
            <h3 className="border-b border-slate-700 pb-2 text-sm font-semibold uppercase tracking-wider text-white">
              Contact
            </h3>
            <ul className="mt-4 space-y-4">
              <ContactRow icon={<Mail className="h-4 w-4" aria-hidden />}>
                <a
                  href="mailto:info@loyyal.com"
                  className="transition-colors hover:text-white"
                >
                  info@loyyal.com
                </a>
              </ContactRow>
              <ContactRow icon={<Phone className="h-4 w-4" aria-hidden />}>
                <a
                  href="tel:+97145134951"
                  className="transition-colors hover:text-white"
                >
                  +971-4-513-4951
                </a>
              </ContactRow>
              {CONTACT_ADDRESSES.map((address) => (
                <ContactRow
                  key={address.text}
                  icon={
                    <span
                      className="flex h-7 w-7 items-center justify-center rounded-full bg-slate-800 text-base leading-none"
                      aria-hidden
                    >
                      {address.flag}
                    </span>
                  }
                >
                  {address.text}
                </ContactRow>
              ))}
            </ul>
          </div>
        </div>

        <div className="mt-12 border-t border-slate-800 pt-8 text-center text-sm text-slate-500">
          &copy; {new Date().getFullYear()} Loyyal. All rights reserved.
        </div>
      </div>
    </footer>
  );
}
